// The three physical storage locations — stock is counted per colour per warehouse,
// split by which factory's codes the cover was made with (F1 = black number, F2 = white
// number in the master Excel). Canonical item field:
//   stock_by_warehouse_factory = { colour: { canada|usa|china: { factory_1: n, factory_2: n } } }
// stock_by_warehouse (totals per warehouse) and stock_by_color are derived by the server.
export const WAREHOUSES = ["canada", "usa", "china"];
export const WAREHOUSE_LABEL = { canada: "Canada", usa: "USA", china: "China" };
export const WAREHOUSE_SHORT = { canada: "CA", usa: "US", china: "CN" };
export const WAREHOUSE_FLAG = { canada: "🇨🇦", usa: "🇺🇸", china: "🇨🇳" };
// Which station takes the part out of each warehouse
export const WAREHOUSE_STATION = { china: "packing", canada: "inventory_packing", usa: "pickup_3pl" };
export const WAREHOUSE_STATION_LABEL = { china: "Packing Orders", canada: "Inventory Packing", usa: "3PL Pick-up" };

export const STOCK_FACTORIES = ["factory_1", "factory_2"];
export const FACTORY_SHORT = { factory_1: "F1", factory_2: "F2" };

export const emptyCell = () => ({ factory_1: 0, factory_2: 0 });
export const emptyGridRow = () => ({ canada: emptyCell(), usa: emptyCell(), china: emptyCell() });
export const emptyWarehouseRow = () => ({ canada: 0, usa: 0, china: 0 });

const n = (v) => Math.max(0, parseInt(v || 0) || 0);

// Canonical grid for an item (falls back to the per-warehouse totals as F1 stock)
export function stockGrid(item) {
  const grid = item?.stock_by_warehouse_factory;
  if (grid && typeof grid === "object") return grid;
  const out = {};
  for (const [c, per] of Object.entries(item?.stock_by_warehouse || {})) {
    out[c] = emptyGridRow();
    for (const w of WAREHOUSES) out[c][w] = { factory_1: n(per?.[w]), factory_2: 0 };
  }
  return out;
}

export const cellTotal = (cell) => n(cell?.factory_1) + n(cell?.factory_2);

// { canada: n, usa: n, china: n } summed across colours + factories
export function warehouseTotals(item) {
  const out = emptyWarehouseRow();
  for (const per of Object.values(stockGrid(item))) {
    for (const w of WAREHOUSES) out[w] += cellTotal(per?.[w]);
  }
  return out;
}

// { factory_1: { total, canada, usa, china }, factory_2: {…} } summed across colours
export function factoryTotals(item) {
  const out = { factory_1: { total: 0, ...emptyWarehouseRow() }, factory_2: { total: 0, ...emptyWarehouseRow() } };
  for (const per of Object.values(stockGrid(item))) {
    for (const w of WAREHOUSES) {
      for (const f of STOCK_FACTORIES) {
        const q = n(per?.[w]?.[f]);
        out[f][w] += q;
        out[f].total += q;
      }
    }
  }
  return out;
}

// Pick-up record on an order (legacy China Warehouse orders mapped onto the same shape)
export function pickupInfo(o) {
  if (o?.pickup?.warehouse) return o.pickup;
  if (o?.china_warehouse) {
    const confirmed = !!o.china_pickup_confirmed;
    return {
      warehouse: "china", factory: o.china_warehouse_factory || o.assigned_factory || "factory_1", station: "packing",
      sent_by: o.china_warehouse_by, sent_at: o.china_warehouse_at, stock_at_send: o.china_stock_at_send,
      confirmed, found: confirmed ? true : null, confirmed_by: o.china_pickup_confirmed_by, confirmed_at: o.china_pickup_confirmed_at, legacy: true,
    };
  }
  return null;
}

export function pickupPending(o) {
  const p = pickupInfo(o);
  return !!(p && !p.confirmed && o?.status === WAREHOUSE_STATION[p.warehouse]);
}
